import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    List,
    ListItem,
    Alert,
    Stack,
    LinearProgress,
    CircularProgress,
    Chip,
    ChipProps,
    Grid,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import {
    CloudUpload as UploadIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    RadioButtonUnchecked as TodoIcon,
    TaskAlt as CheckedIcon,
} from '@mui/icons-material';
import Papa from 'papaparse';
import { supabase } from '../providers/supabase/supabase';
import {
    normalizeGreatAmerican,
    normalizeProAg,
    normalizeRCIS,
    normalizeAgriSompo,
    normalizeRainHail,
    normalizeNAU,
} from '../utils/rateNormalization';
import { useRatesImport, RateImportSchema } from './useRatesImport';
import {
    useCropClassesImport,
    CropClassImportSchema,
    normalizeCropClasses,
} from './useCropClassesImport';
import {
    useCropCoveragesImport,
    CropCoverageImportSchema,
    normalizeCropCoverages,
} from './useCropCoveragesImport';
import {
    normalizeEndorsementDefs,
    useEndorsementDefsImport,
} from './useEndorsementDefsImport';
import {
    normalizeNAUEndorsements,
    normalizeProAgEndorsements,
    EndorsementNormalizationContext,
} from '../utils/endorsementNormalization';
import { useEndorsementRatesImport } from './useEndorsementRatesImport';
import {
    useDataProvider,
    useRefresh,
    Form,
    Toolbar,
    Button as RaButton,
    RaRecord,
} from 'react-admin';

const carriers = {
    greatAmerican: ['ID', 'OR', 'WA'],
    NAU: ['ID', 'MT', 'OR', 'WA', 'WY'],
    proAg: ['ID', 'WA', 'MT'],
    RCIS: ['WY', 'MT'],
    AgriSompo: ['ID', 'MT', 'OR', 'WA'],
    rainHail: [
        'ID',
        'ID - Special',
        'WA',
        'WA - peasRat',
        'OR',
        'MT',
        'OR - Fruit',
        'WA',
        'wa2020',
    ],
};

const exampleRcisCSV = `AIP,CropYear,StateCode,State,CountyCode,County,Township,Range,FormCode,Form,A,B,C,E
RCIS,2025,30,MT,001,County1,014N,073W,0,BASIC,18,6.5,3.3,8.5`;

const exampleRainHailCSV = `// Add RainHail example format`;

// Define the list of required spreadsheets
const REQUIRED_SPREADSHEETS = [
    'AgriSompo-ID-Class-All.csv',
    'AgriSompo-MT-Class-A-P2.csv',
    'AgriSompo-OR-Class-A-P2.csv',
    'AgriSompo-WA-Class-A-P2.csv',
    'Coverage Definitions.csv',
    'Crop Class - Crop - Class -State - Carrier.csv',
    'greatAmerican-ID-Class-All.csv',
    'greatAmerican-OR-Class-All.csv',
    'greatAmerican-WA-Class-All.csv',
    'NAU-ID-Class-All.csv',
    'NAU-MT-Class-All.csv',
    'NAU-OR-Class-All.csv',
    'NAU-WA-Class-All.csv',
    'NAU-WY-Class-All.csv',
    'proAg-ID-Class-All.csv',
    'proAg-MT-Class-All.csv',
    'proAg-WA-Class-All.csv',
    'rainHail-ID-Class-A-N.csv',
    'rainHail-ID-Class-H-K.csv',
    'rainHail-MT-Class-A-M.csv',
    'rainHail-OR-Class-A-M.csv',
    'rainHail-WA-Class-A-P2.csv',
    'rainHail-WA-Class-D-K.csv',
    'RCIS-ID-Class-A-P.csv',
    'RCIS-MT-Class-All.csv',
    'RCIS-OR-Class-A-P.csv',
    'RCIS-WY-Class-All.csv',
    'greatAmerican-ID-endorsements.csv',
    'greatAmerican-WA-endorsements.csv',
    'NAU-ID-endorsements.csv',
    'NAU-OR-endorsements.csv',
    'proAg-ID-endorsements.csv',
    'proAg-WA-endorsements.csv',
    'Endorsement Definitions.csv',
];

const databaseTableStructure = [
    { key: 'AIP', description: 'Code for the carrier' },
    { key: 'State', description: 'State the rates are for' },
    { key: 'Crop Year', description: 'Year the rates are for' },
    { key: 'County Code', description: 'Optional county code integer' },
    { key: 'County', description: 'County in the state' },
    { key: 'Township', description: '' },
    { key: 'Range', description: '' },
    {
        key: 'Policy Form Name',
        description: 'One Of: Basic, DXS5, DXS10, XS10IP, Comp 2, Comp 3',
    },
    {
        key: 'Crop Category',
        description:
            'Category of Crop type: A, B, C, D, E, F, G, H, I, J, K, O, P1, P2',
    },
];

function millisecondsToTime(ms: number): string {
    if (isNaN(ms) || ms < 0) return '0m 0s';
    var seconds = Math.floor((ms / 1000) % 60);
    var minutes = Math.floor((ms / (60 * 1000)) % 60);
    return `${minutes}m ${seconds}s`;
}

type SpreadsheetType =
    | 'rates'
    | 'cropClasses'
    | 'cropCoverages'
    | 'endorsements'
    | 'endorsementDefs'
    | 'unknown';

// Type for the status data fetched from the database
interface RateSheetStatus extends RaRecord {
    filename: string;
    status: 'todo' | 'uploaded' | 'error' | 'checked';
    last_updated: string;
    error_message?: string;
}

// Helper to group filenames by carrier/type
const groupSpreadsheets = (filenames: string[]): Record<string, string[]> => {
    const groups: Record<string, string[]> = {};
    const carrierPrefixes = [
        'AgriSompo',
        'greatAmerican',
        'NAU',
        'proAg',
        'rainHail',
        'RCIS',
    ];

    filenames.forEach(filename => {
        let groupName = 'Other'; // Default group

        if (filename.includes('Crop Class')) {
            groupName = 'Crop Classes';
        } else if (filename.includes('Coverage Definitions')) {
            groupName = 'Crop Coverages';
        } else if (filename.includes('Endorsement Definitions')) {
            groupName = 'Endorsements';
        } else if (filename.toLowerCase().includes('endorsement')) {
            groupName = 'Endorsements';
        } else {
            for (const prefix of carrierPrefixes) {
                if (filename.startsWith(prefix)) {
                    groupName = prefix;
                    break;
                }
            }
        }

        if (!groups[groupName]) {
            groups[groupName] = [];
        }
        groups[groupName].push(filename);
    });

    // Sort filenames within each group
    for (const groupName in groups) {
        groups[groupName].sort();
    }

    // Optional: Sort group names (e.g., alphabetically, or custom order)
    const sortedGroupNames = Object.keys(groups).sort((a, b) => {
        // Custom sort order example
        const order = [
            'Crop Classes',
            'Crop Coverages',
            'Endorsements',
            'AgriSompo',
            'greatAmerican',
            'NAU',
            'proAg',
            'rainHail',
            'RCIS',
            'Other',
        ];
        return order.indexOf(a) - order.indexOf(b);
    });

    const sortedGroups: Record<string, string[]> = {};
    sortedGroupNames.forEach(name => {
        sortedGroups[name] = groups[name];
    });

    return sortedGroups;
};

// Refactored Helper to determine parser info from filename
const determineParserInfo = (
    filename: string
): { type: SpreadsheetType; key: string; display: string } => {
    const lowerFilename = filename.toLowerCase();

    if (
        lowerFilename.includes('endorsement definitions') ||
        lowerFilename.includes('endorsement_definitions')
    ) {
        return {
            type: 'endorsementDefs' as SpreadsheetType,
            key: 'endorsementDefs',
            display: 'Endorsement Definitions Importer',
        };
    }
    if (lowerFilename.includes('endorsement')) {
        let carrier = 'unknown';
        if (lowerFilename.includes('nau')) carrier = 'NAU';
        else if (lowerFilename.includes('proag')) carrier = 'proAg';
        else if (lowerFilename.includes('rainhail')) carrier = 'rainHail';
        else if (lowerFilename.includes('greatamerican'))
            carrier = 'Great American';
        // Extract state abbreviation from filename (e.g., "proAg-ID-endorsement" -> "ID")
        const stateMatch = lowerFilename.match(/[-_](id|mt|or|wa|wy)[-_]/i);
        const stateFromFile = stateMatch ? stateMatch[1].toUpperCase() : '';
        return {
            type: 'endorsements' as SpreadsheetType,
            key: `${carrier}|${stateFromFile}`,
            display: `${carrier} ${stateFromFile} Endorsement Rates`,
        };
    }
    if (lowerFilename.includes('crop class')) {
        return {
            type: 'cropClasses',
            key: 'cropClasses',
            display: 'Crop Class Importer',
        };
    }
    if (lowerFilename.includes('coverage definitions')) {
        return {
            type: 'cropCoverages',
            key: 'cropCoverages',
            display: 'Coverage Definition Importer',
        };
    }

    let parserKey = 'unknown';
    let displayKey = 'Unknown/Generic Rates';

    if (lowerFilename.includes('agrisompo')) {
        parserKey = 'AgriSompo';
        displayKey = 'AgriSompo Rates';
    } else if (lowerFilename.includes('greatamerican')) {
        parserKey = 'greatAmerican';
        displayKey = 'Great American Rates';
    } else if (lowerFilename.includes('nau')) {
        parserKey = 'NAU';
        displayKey = 'NAU Rates';
    } else if (lowerFilename.includes('proag')) {
        parserKey = 'proAg';
        displayKey = 'ProAg Rates';
    } else if (lowerFilename.includes('rcis')) {
        parserKey = 'RCIS';
        displayKey = 'RCIS Rates';
    } else if (
        lowerFilename.includes('rainhail') ||
        lowerFilename.includes('rh -')
    ) {
        parserKey = 'rainHail';
        displayKey = 'Rain & Hail Rates';
    }

    if (parserKey === 'unknown') {
        return {
            type: 'rates',
            key: 'unmatched',
            display: 'Missing Algorithm',
        };
    }

    return { type: 'rates', key: parserKey, display: displayKey };
};

const RateManagementPage = () => {
    const [file, setFile] = useState<File | null>(null);
    const [selectedFilename, setSelectedFilename] = useState<string | null>(
        null
    );
    const [determinedParser, setDeterminedParser] = useState<{
        type: SpreadsheetType;
        key: string;
        display: string;
    } | null>(null);
    const [uploadBlockedReason, setUploadBlockedReason] = useState<
        string | null
    >(null);
    const [previewData, setPreviewData] = useState<
        Array<
            RateImportSchema | CropClassImportSchema | CropCoverageImportSchema
        >
    >([]);
    const [logs, setLogs] = useState<string[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [processedRecordCount, setProcessedRecordCount] = useState(0);
    const [totalRecordCount, setTotalRecordCount] = useState(0);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [sheetStatuses, setSheetStatuses] = useState<
        Record<string, RateSheetStatus>
    >({});
    const dataProvider = useDataProvider();
    const refresh = useRefresh();

    const saveRatesBatch = useRatesImport(selectedFilename || '');
    const saveCropClassesBatch = useCropClassesImport(selectedFilename || '');
    const saveCropCoveragesBatch = useCropCoveragesImport(
        selectedFilename || ''
    );
    const saveEndorsementBatch = useEndorsementRatesImport(
        selectedFilename || ''
    );
    const saveEndorsementDefsBatch = useEndorsementDefsImport(
        selectedFilename || ''
    );

    // Fetch statuses on mount via supabase directly (table uses filename as PK, not id)
    const [statusesLoading, setStatusesLoading] = useState(true);
    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const { data, error } = await supabase
                    .from('rate_sheet_status')
                    .select('*')
                    .order('filename');
                if (error) throw error;
                const statuses: Record<string, RateSheetStatus> = {};
                (data || []).forEach(
                    (item: {
                        filename: string;
                        status: string;
                        last_updated: string;
                        error_message?: string;
                    }) => {
                        statuses[item.filename] = {
                            ...item,
                            id: item.filename,
                        } as RateSheetStatus;
                    }
                );
                setSheetStatuses(statuses);
            } catch (err: any) {
                console.error('Error fetching rate sheet statuses:', err);
                setLogs(prev => [
                    ...prev,
                    `Error fetching statuses: ${err.message}`,
                ]);
            } finally {
                setStatusesLoading(false);
            }
        };
        fetchStatuses();
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv'],
        },
        onDrop: acceptedFiles => {
            if (acceptedFiles.length > 0) {
                handleFileChange(acceptedFiles[0]);
            }
        },
        disabled: isUploading,
    });

    const normalizeData = useCallback(
        (data: Record<string, any>[], filename: string): RateImportSchema[] => {
            console.log(
                `Normalizing ${data.length} records based on filename: ${filename}`
            );
            const parserInfo = determineParserInfo(filename); // Use helper
            const carrierKey = parserInfo.key;
            let normalized: Record<string, any>[] = [];
            const dataArray = Array.isArray(data) ? data : [data];
            if (dataArray.length === 0) return [];

            // Extract state from filename. Supports patterns like:
            //   "carrier-STATE-..." (e.g. rainHail-OR-compRates.csv)
            //   "carrier-2026 STATE Rates" (e.g. agriSompo-2026 OR Rates.csv)
            let state = 'unknown';
            const nameParts = filename.replace('.csv', '').split('-');
            if (
                nameParts.length > 1 &&
                nameParts[1].length === 2 &&
                /^[A-Z]{2}$/.test(nameParts[1])
            ) {
                state = nameParts[1];
            } else {
                // Scan all filename parts/words for a 2-letter state code
                const US_STATES =
                    /^(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY)$/;
                const words = filename.replace('.csv', '').split(/[\s\-_]+/);
                for (const word of words) {
                    if (US_STATES.test(word)) {
                        state = word;
                        break;
                    }
                }
            }

            try {
                switch (carrierKey) {
                    case 'greatAmerican':
                        normalized = normalizeGreatAmerican(dataArray, state);
                        break;
                    case 'NAU':
                        normalized = normalizeNAU(dataArray, state);
                        break;
                    case 'proAg':
                        normalized = normalizeProAg(dataArray, state);
                        break;
                    case 'RCIS':
                        normalized = normalizeRCIS(dataArray, state);
                        break;
                    case 'AgriSompo':
                        normalized = normalizeAgriSompo(dataArray, state);
                        break;
                    case 'rainHail':
                        normalized = normalizeRainHail(
                            dataArray,
                            state,
                            filename
                        );
                        break;
                    default:
                        console.warn(
                            `No normalization algorithm for derived key: ${carrierKey} from file ${filename}. Using raw data.`
                        );
                        normalized = dataArray;
                        break;
                }
            } catch (error) {
                console.error(
                    `Error during specific normalization for ${carrierKey} (file: ${filename}):`,
                    error
                );
                normalized = dataArray;
            }
            return normalized.map(row => {
                const getStr = (key: string, defaultValue = '') =>
                    row && typeof row === 'object' && row[key] != null
                        ? String(row[key]).trim()
                        : defaultValue;
                return {
                    carrier: getStr('carrier', carrierKey),
                    cropyear: getStr('cropyear'),
                    state: filename.includes('proAg-WA')
                        ? 'WA'
                        : getStr('state', state),
                    countycode: getStr('countycode'),
                    county: getStr('county'),
                    township: getStr('township'),
                    range: getStr('range'),
                    form: getStr('form'),
                    crop: getStr('crop'),
                    a: getStr('a'),
                    b: getStr('b'),
                    c: getStr('c'),
                    d: getStr('d'),
                    e: getStr('e'),
                    f: getStr('f'),
                    g: getStr('g'),
                    h: getStr('h'),
                    i: getStr('i'),
                    j: getStr('j'),
                    k: getStr('k'),
                    l: getStr('l'),
                    m: getStr('m'),
                    n: getStr('n'),
                    o: getStr('o'),
                    p: getStr('p'),
                    p1: getStr('p1'),
                    p2: getStr('p2'),
                    a1: getStr('a1'),
                    a2: getStr('a2'),
                    r1: getStr('r1'),
                    r2: getStr('r2'),
                    e2: getStr('e2'),
                } as RateImportSchema;
            });
        },
        []
    );

    const handleFileChange = useCallback(
        (selectedFile: File | null) => {
            console.log(
                '[handleFileChange] Selected file object:',
                selectedFile
            );
            if (!selectedFile) {
                setFile(null);
                setSelectedFilename(null);
                setDeterminedParser(null);
                setUploadBlockedReason(null);
                setPreviewData([]);
                setLogs([]);
                setPreviewError(null);
                setUploadError(null);
                setUploadSuccess(false);
                setIsUploading(false);
                return;
            }

            setFile(selectedFile);
            const filename = selectedFile.name;
            setSelectedFilename(filename);
            const parserInfo = determineParserInfo(filename);
            setDeterminedParser(parserInfo);

            if (parserInfo.key === 'unmatched') {
                setUploadBlockedReason(
                    `Algorithm not found for file: ${filename}`
                );
                setLogs(prev => [
                    ...prev,
                    `WARNING: No specific algorithm found for ${filename}. Upload blocked.`,
                ]);
            } else {
                setUploadBlockedReason(null); // Clear block if algorithm found
            }

            const currentType = parserInfo.type;

            setPreviewData([]);
            setPreviewError(null);
            setUploadError(null);
            setUploadSuccess(false);
            setIsUploading(false);
            setLogs([
                `File selected: ${filename}. Determined type: ${currentType}. Parser: ${parserInfo.display}. Parsing preview...`,
            ]);
            setIsPreviewLoading(true);

            if (selectedFile) {
                console.log(
                    `[handleFileChange] Attempting to read file: ${selectedFile.name}, size: ${selectedFile.size}`
                );
                const reader = new FileReader();
                reader.onload = e => {
                    const textContent = e.target?.result as string;
                    console.log(
                        `[handleFileChange] FileReader read ${textContent?.length || 0} characters.`
                    );
                    console.log(
                        '[handleFileChange] File content snippet (first 500 chars):',
                        textContent?.substring(0, 500)
                    );

                    // Proceed with PapaParse after logging content
                    Papa.parse<Record<string, any>>(selectedFile!, {
                        header: true,
                        skipEmptyLines: true,
                        preview: 150,
                        dynamicTyping: true,
                        complete: (
                            results: Papa.ParseResult<Record<string, any>>
                        ) => {
                            // --- DEBUG LOGGING (can be reinstated if needed) ---
                            // console.log('PapaParse results (full):', results);
                            // if (results.errors && results.errors.length > 0) {
                            //     console.error('PapaParse errors:', results.errors);
                            // }
                            // --- END DEBUG LOGGING ---
                            const previewRowCount = results.data.length;
                            const logEntry = `Parsed ${previewRowCount} rows for preview. Normalizing...`;
                            setLogs(prev => [logEntry]);
                            try {
                                let normalizedPreview: any[] = [];
                                if (currentType === 'cropClasses') {
                                    normalizedPreview = normalizeCropClasses(
                                        results.data
                                    );
                                } else if (currentType === 'cropCoverages') {
                                    normalizedPreview = normalizeCropCoverages(
                                        results.data
                                    );
                                } else if (currentType === 'endorsementDefs') {
                                    normalizedPreview =
                                        normalizeEndorsementDefs(results.data);
                                } else if (currentType === 'rates') {
                                    normalizedPreview = normalizeData(
                                        results.data,
                                        filename
                                    );
                                } else if (currentType === 'endorsements') {
                                    // For preview, just show raw parsed data
                                    normalizedPreview = results.data.slice(
                                        0,
                                        20
                                    ) as any[];
                                } else {
                                    setPreviewError(
                                        'Could not determine spreadsheet type for normalization.'
                                    );
                                }
                                setPreviewData(
                                    normalizedPreview as Array<
                                        | RateImportSchema
                                        | CropClassImportSchema
                                        | CropCoverageImportSchema
                                    >
                                );
                                setLogs(prev => [...prev, 'Preview ready.']);
                            } catch (normError) {
                                const errorMsg =
                                    normError instanceof Error
                                        ? normError.message
                                        : 'Unknown error';
                                console.error(
                                    '[handleFileChange] Error normalizing preview (after FileReader):',
                                    normError
                                );
                                setLogs(prev => [
                                    ...prev,
                                    `Error normalizing preview (after FileReader): ${errorMsg}`,
                                ]);
                                setPreviewError(
                                    `Error preparing preview (after FileReader): ${errorMsg}`
                                );
                            }
                            setIsPreviewLoading(false);
                        },
                        error: (error: Papa.ParseError | Error) => {
                            const message =
                                (error as Papa.ParseError)?.message ||
                                (error as Error)?.message ||
                                'Unknown parsing error';
                            console.error(
                                '[handleFileChange] PapaParse error (after FileReader):',
                                error
                            );
                            setLogs(prev => [
                                ...prev,
                                `Error parsing CSV (after FileReader): ${message}`,
                            ]);
                            setPreviewError(
                                `Error parsing CSV (after FileReader): ${message}`
                            );
                            setIsPreviewLoading(false);
                        },
                    });
                };
                reader.onerror = e => {
                    console.error(
                        '[handleFileChange] FileReader error while trying to read file:',
                        e
                    );
                    // Attempt to parse anyway, as PapaParse might handle some cases or have its own errors
                    Papa.parse<Record<string, any>>(selectedFile!, {
                        header: true,
                        skipEmptyLines: true,
                        preview: 150,
                        dynamicTyping: true,
                        complete: (
                            results: Papa.ParseResult<Record<string, any>>
                        ) => {
                            console.warn(
                                '[handleFileChange] PapaParse complete callback after FileReader error.'
                            );
                            const previewRowCount = results.data.length;
                            const logEntry = `(After FileReader error) Parsed ${previewRowCount} rows for preview. Normalizing...`;
                            setLogs(prev => [logEntry]);
                            try {
                                let normalizedPreview: any[] = [];
                                if (currentType === 'cropClasses') {
                                    normalizedPreview = normalizeCropClasses(
                                        results.data
                                    );
                                } else if (currentType === 'cropCoverages') {
                                    normalizedPreview = normalizeCropCoverages(
                                        results.data
                                    );
                                } else if (currentType === 'endorsementDefs') {
                                    normalizedPreview =
                                        normalizeEndorsementDefs(results.data);
                                } else if (currentType === 'rates') {
                                    normalizedPreview = normalizeData(
                                        results.data,
                                        filename!
                                    );
                                } else if (currentType === 'endorsements') {
                                    // For preview, just show raw parsed data
                                    normalizedPreview = results.data.slice(
                                        0,
                                        20
                                    ) as any[];
                                } else {
                                    setPreviewError(
                                        'Could not determine spreadsheet type for normalization.'
                                    );
                                }
                                setPreviewData(
                                    normalizedPreview as Array<
                                        | RateImportSchema
                                        | CropClassImportSchema
                                        | CropCoverageImportSchema
                                    >
                                );
                                setLogs(prev => [
                                    ...prev,
                                    'Preview ready (after FileReader error).',
                                ]);
                            } catch (normError) {
                                const errorMsg =
                                    normError instanceof Error
                                        ? normError.message
                                        : 'Unknown error';
                                console.error(
                                    '[handleFileChange] Error normalizing preview (after FileReader error):',
                                    normError
                                );
                                setLogs(prev => [
                                    ...prev,
                                    `Error normalizing preview (after FileReader error): ${errorMsg}`,
                                ]);
                                setPreviewError(
                                    `Error preparing preview (after FileReader error): ${errorMsg}`
                                );
                            }
                            setIsPreviewLoading(false);
                        },
                        error: (error: Papa.ParseError | Error) => {
                            const message =
                                (error as Papa.ParseError)?.message ||
                                (error as Error)?.message ||
                                'Unknown parsing error';
                            console.error(
                                '[handleFileChange] PapaParse error (after FileReader error):',
                                error
                            );
                            setLogs(prev => [
                                ...prev,
                                `Error parsing CSV (after FileReader error): ${message}`,
                            ]);
                            setPreviewError(
                                `Error parsing CSV (after FileReader error): ${message}`
                            );
                            setIsPreviewLoading(false);
                        },
                    });
                };
                reader.readAsText(selectedFile, 'UTF-8');
            } else {
                // This case should ideally not be reached if selectedFile is null due to earlier checks
                // but as a fallback, ensure preview loading is false.
                setIsPreviewLoading(false);
            }
        },
        [normalizeData]
    );

    const handleStartFullImport = async () => {
        if (!file || uploadBlockedReason) return; // Add check for uploadBlockedReason
        setIsUploading(true);
        const filename = file.name;
        const parserInfo = determineParserInfo(filename);
        const currentType = parserInfo.type;

        setLogs([
            `Starting full upload for ${filename} as type ${currentType}...`,
        ]);
        setPreviewData([]);
        setUploadError(null);
        setUploadSuccess(false);
        setProcessedRecordCount(0);
        setTotalRecordCount(0);
        setUploadProgress(0);

        try {
            Papa.parse<Record<string, any>>(file!, {
                header: true,
                skipEmptyLines: true,
                dynamicTyping: true,
                complete: async (
                    results: Papa.ParseResult<Record<string, any>>
                ) => {
                    setLogs(prev => [
                        ...prev,
                        `File parsing complete. Found ${results.data.length} data rows.`,
                    ]);
                    const totalRows = results.data.length;
                    setTotalRecordCount(totalRows);
                    let currentProcessedCount = 0;

                    if (results.errors.length > 0) {
                        console.error('CSV Parsing errors:', results.errors);
                        setLogs(prev => [
                            ...prev,
                            `WARNING: Encountered ${results.errors.length} parsing errors.`,
                        ]);
                        // Decide if you want to proceed despite parsing errors
                    }

                    try {
                        setLogs(prev => [
                            ...prev,
                            `Normalizing all data for ${currentType}...`,
                        ]);
                        let normalizedFullData: any[] = []; // Simplified type
                        if (currentType === 'cropClasses') {
                            normalizedFullData = normalizeCropClasses(
                                results.data
                            );
                        } else if (currentType === 'cropCoverages') {
                            normalizedFullData = normalizeCropCoverages(
                                results.data
                            );
                        } else if (currentType === 'endorsementDefs') {
                            normalizedFullData = normalizeEndorsementDefs(
                                results.data
                            );
                        } else if (currentType === 'rates') {
                            normalizedFullData = normalizeData(
                                results.data,
                                filename
                            );
                        } else if (currentType === 'endorsements') {
                            // Extract carrier + state from parserInfo.key (format: "carrier|state")
                            const [fileCarrier, fileState] = (
                                parserInfo.key || ''
                            ).split('|');

                            // For endorsements, map raw CSV rows to the endorsement schema
                            // Resolve endorsement alias to canonical name using endorsement_definitions
                            let endoDefs: any[] = [];
                            try {
                                const { data: defsData } =
                                    await dataProvider.getList(
                                        'endorsement_definitions',
                                        {
                                            pagination: {
                                                page: 1,
                                                perPage: 1000,
                                            },
                                            sort: {
                                                field: 'id',
                                                order: 'ASC',
                                            },
                                            filter: {},
                                        }
                                    );
                                endoDefs = defsData || [];
                            } catch (defErr) {
                                console.error(
                                    'Failed to fetch endorsement definitions for alias resolution:',
                                    defErr
                                );
                            }

                            const resolveAlias = (rawName: string): string => {
                                if (!rawName) return rawName;
                                const trimmed = rawName.trim();
                                // Check if it's already a canonical name
                                const exactMatch = endoDefs.find(
                                    (d: any) => d.endorsement === trimmed
                                );
                                if (exactMatch) return exactMatch.endorsement;
                                // Check aliases
                                for (const def of endoDefs) {
                                    if (
                                        def.endorsement_alias?.includes(trimmed)
                                    ) {
                                        return def.endorsement;
                                    }
                                }
                                // Return as-is if no match (import hook will validate)
                                return trimmed;
                            };

                            // Fetch coverage definitions for "All" form expansion
                            let allCoverages: any[] = [];
                            try {
                                const { data: covData } =
                                    await dataProvider.getList(
                                        'crop_coverages',
                                        {
                                            pagination: {
                                                page: 1,
                                                perPage: 1000,
                                            },
                                            sort: {
                                                field: 'coverage',
                                                order: 'ASC',
                                            },
                                            filter: {},
                                        }
                                    );
                                allCoverages = covData || [];
                            } catch (covErr) {
                                console.error(
                                    'Failed to fetch coverages for form expansion:',
                                    covErr
                                );
                            }

                            // Build carrier-state -> forms lookup
                            const endoCarrierTagMap: Record<string, string> = {
                                RCIS: 'RCIS',
                                NAU: 'NAU',
                                'Rain & Hail': 'rainHail',
                                AgriSompo: 'agriSompo',
                                'Great American': 'greatAmerican',
                                proAg: 'proAg',
                            };
                            const getFormsForCarrierState = (
                                carrier: string,
                                state: string
                            ): string[] => {
                                const tagPrefix =
                                    endoCarrierTagMap[carrier] || carrier;
                                const stateAbbr =
                                    state.length === 2
                                        ? state
                                        : state.substring(0, 2);
                                const targetTag = `${tagPrefix}-${stateAbbr}`;
                                return allCoverages
                                    .filter((c: any) =>
                                        c.tags?.includes(targetTag)
                                    )
                                    .map((c: any) => c.coverage as string);
                            };

                            // Detect pivoted format: crop names as column headers
                            // (e.g., Great American: AIP, State, ..., Endorsement, Wheat, Barley, Peas)
                            const standardCols = new Set([
                                'aip',
                                'state',
                                'crop year',
                                'county code',
                                'county',
                                'township',
                                'range',
                                'endorsement',
                                'endorsement name',
                                'form',
                                'form name',
                                'rate',
                                'crop',
                                'class',
                                'carrier',
                                'crop_year',
                                'county_code',
                            ]);
                            const csvHeaders = Object.keys(
                                results.data[0] || {}
                            );
                            const cropColumns = csvHeaders.filter(
                                h =>
                                    !standardCols.has(
                                        h
                                            .toLowerCase()
                                            .replace(/[^\w\s]/g, '')
                                            .trim()
                                    ) && h.trim() !== ''
                            );
                            const hasCropColumn = csvHeaders.some(
                                h =>
                                    h.toLowerCase() === 'crop' ||
                                    h.toLowerCase() === 'crop name'
                            );
                            const isPivoted =
                                !hasCropColumn && cropColumns.length > 0;

                            if (isPivoted) {
                                setLogs(prev => [
                                    ...prev,
                                    `Detected pivoted format with crop columns: ${cropColumns.join(', ')}`,
                                ]);
                            }

                            // Process rows, expanding "All" forms
                            const expanded: any[] = [];
                            const seen = new Set<string>(); // dedupe key
                            for (const row of results.data) {
                                const rawEndo =
                                    row.endorsement ||
                                    row.Endorsement ||
                                    row['Endorsement Name'] ||
                                    '';
                                const carrier =
                                    row.AIP ||
                                    row.aip ||
                                    row.carrier ||
                                    row.Carrier ||
                                    fileCarrier ||
                                    '';
                                // Clean BOM from carrier if present
                                const carrierClean = carrier
                                    .replace(/^\uFEFF/, '')
                                    .trim();
                                const state = (
                                    row.state ||
                                    row.State ||
                                    fileState ||
                                    ''
                                ).trim();
                                const county = (
                                    row.county ||
                                    row.County ||
                                    row.COUNTY ||
                                    ''
                                ).trim();
                                const countyCode = (
                                    row.county_code ||
                                    row['County Code'] ||
                                    row['COUNTY CODE'] ||
                                    ''
                                ).trim();
                                const township = (
                                    row.township ||
                                    row.Township ||
                                    row.TOWNSHIP ||
                                    ''
                                ).trim();
                                const rangeVal = (
                                    row.range ||
                                    row.Range ||
                                    row.RANGE ||
                                    ''
                                ).trim();
                                const endorsement = resolveAlias(rawEndo);
                                const cropYear = (
                                    row.crop_year ||
                                    row.CropYear ||
                                    row['Crop Year'] ||
                                    '2026'
                                ).trim();

                                if (
                                    !carrierClean ||
                                    !state ||
                                    !county ||
                                    !endorsement
                                )
                                    continue;

                                if (isPivoted) {
                                    // Pivoted: each crop column is a separate rate
                                    for (const cropCol of cropColumns) {
                                        const rateStr = (row[cropCol] || '')
                                            .toString()
                                            .replace(/[$,\s]/g, '')
                                            .trim();
                                        if (!rateStr) continue;
                                        const rate = parseFloat(rateStr);
                                        if (isNaN(rate)) continue;

                                        // Dedupe: same location+crop+endorsement
                                        const dedupeKey = `${carrierClean}|${state}|${county}|${township}|${rangeVal}|${cropCol}|${endorsement}`;
                                        if (seen.has(dedupeKey)) continue;
                                        seen.add(dedupeKey);

                                        // No form column in pivoted format — store as "All" wildcard
                                        // HPT lookup treats form="All" as matching any hail product
                                        expanded.push({
                                            carrier: carrierClean,
                                            crop_year: cropYear,
                                            state,
                                            county_code: countyCode,
                                            county,
                                            crop: cropCol,
                                            form: 'All',
                                            endorsement,
                                            rate,
                                        });
                                    }
                                    continue; // skip flat-format processing
                                }

                                // Flat format (NAU-style): one crop per row
                                const crop = (
                                    row.crop ||
                                    row.Crop ||
                                    ''
                                ).trim();
                                const formRaw = (
                                    row.form ||
                                    row.Form ||
                                    row['Form Name'] ||
                                    ''
                                ).trim();
                                const rate = parseFloat(
                                    (row.rate || row.Rate || '0')
                                        .toString()
                                        .replace(/[$,\s]/g, '')
                                );

                                if (!crop || !endorsement || isNaN(rate))
                                    continue;

                                if (
                                    !carrier ||
                                    !state ||
                                    !county ||
                                    !crop ||
                                    !endorsement ||
                                    isNaN(rate)
                                )
                                    continue;

                                if (
                                    formRaw.toLowerCase() === 'all' ||
                                    formRaw === '0' ||
                                    formRaw === ''
                                ) {
                                    // Store as "All" wildcard — HPT lookup treats this as matching any hail product
                                    expanded.push({
                                        carrier: carrierClean,
                                        crop_year: cropYear,
                                        state,
                                        county_code: countyCode,
                                        county,
                                        crop,
                                        form: 'All',
                                        endorsement,
                                        rate,
                                    });
                                } else {
                                    expanded.push({
                                        carrier: carrierClean,
                                        crop_year: cropYear,
                                        state,
                                        county_code: countyCode,
                                        county,
                                        crop,
                                        form: formRaw,
                                        endorsement,
                                        rate,
                                    });
                                }
                            }
                            normalizedFullData = expanded;

                            setLogs(prev => [
                                ...prev,
                                `Endorsement normalization: ${results.data.length} CSV rows expanded to ${expanded.length} records (Form "All" expanded).`,
                            ]);
                        } else {
                            throw new Error(
                                'Cannot process file: Unknown spreadsheet type.'
                            );
                        }
                        setLogs(prev => [
                            ...prev,
                            `Normalization complete. Processing ${normalizedFullData.length} records...`,
                        ]);

                        const BATCH_SIZE = 100;
                        let errorCount = 0;

                        let importFunction: (
                            batch: any[],
                            filename: string
                        ) => Promise<void>;
                        if (currentType === 'cropClasses') {
                            importFunction = saveCropClassesBatch;
                        } else if (currentType === 'cropCoverages') {
                            importFunction = saveCropCoveragesBatch;
                        } else if (currentType === 'endorsementDefs') {
                            importFunction = async (
                                batch: any[],
                                _filename: string
                            ) => {
                                await saveEndorsementDefsBatch(batch);
                            };
                        } else if (currentType === 'endorsements') {
                            importFunction = async (
                                batch: any[],
                                _filename: string
                            ) => {
                                await saveEndorsementBatch(batch);
                            };
                        } else {
                            importFunction = saveRatesBatch;
                        }

                        const dataToProcess = normalizedFullData as any[];

                        for (
                            let i = 0;
                            i < dataToProcess.length;
                            i += BATCH_SIZE
                        ) {
                            const chunk = dataToProcess.slice(
                                i,
                                i + BATCH_SIZE
                            );
                            try {
                                await importFunction(chunk, filename);
                                currentProcessedCount += chunk.length;
                                setProcessedRecordCount(currentProcessedCount);
                                setUploadProgress(
                                    totalRows > 0
                                        ? (currentProcessedCount / totalRows) *
                                              100
                                        : 0
                                );
                            } catch (batchError) {
                                const errorMsg =
                                    batchError instanceof Error
                                        ? batchError.message
                                        : 'Unknown batch error';
                                console.error(
                                    `Error saving batch for ${filename} starting at index ${i}:`,
                                    batchError
                                );
                                setLogs(prev => [
                                    ...prev,
                                    `ERROR saving batch ${Math.floor(i / BATCH_SIZE) + 1}: ${errorMsg}`,
                                ]);
                                errorCount += chunk.length; // Count entire batch as error for simplicity
                                // Decide if you want to stop on first batch error:
                                // setUploadError(`Failed during batch ${Math.floor(i / BATCH_SIZE) + 1}: ${errorMsg}`);
                                // throw batchError; // Uncomment to stop processing
                            }
                        }

                        setLogs(prev => [
                            ...prev,
                            `Upload processing finished. Processed: ${currentProcessedCount}, Errors: ${errorCount}`,
                        ]);
                        setUploadSuccess(errorCount === 0);
                        if (errorCount > 0) {
                            setUploadError(
                                `Upload completed with ${errorCount} errors (check logs).`
                            );
                        }

                        refresh(); // Refresh admin state

                        // Refresh materialized views so HPT dropdowns reflect new data
                        setLogs(prev => [
                            ...prev,
                            'Refreshing materialized views...',
                        ]);
                        try {
                            await supabase.rpc('refresh_matviews');
                            setLogs(prev => [
                                ...prev,
                                'Materialized views refreshed successfully.',
                            ]);
                        } catch (matviewErr) {
                            console.error(
                                'Failed to refresh materialized views:',
                                matviewErr
                            );
                            setLogs(prev => [
                                ...prev,
                                'Warning: Could not refresh materialized views. HPT dropdowns may need a manual refresh.',
                            ]);
                        }
                    } catch (normError) {
                        const errorMsg =
                            normError instanceof Error
                                ? normError.message
                                : 'Unknown normalization error';
                        console.error(
                            'Error normalizing full data:',
                            normError
                        );
                        setLogs(prev => [
                            ...prev,
                            `Error normalizing full data: ${errorMsg}`,
                        ]);
                        setUploadError(`Normalization Failed: ${errorMsg}`);
                    }
                    setIsUploading(false); // Mark upload as finished
                },
                error: (error: Papa.ParseError | Error) => {
                    const message =
                        (error as Papa.ParseError)?.message ||
                        (error as Error)?.message ||
                        'Unknown parsing error';
                    console.error(
                        'Critical error parsing CSV for full upload:',
                        error
                    );
                    setLogs(prev => [
                        ...prev,
                        `CRITICAL PARSE ERROR: ${message}`,
                    ]);
                    setUploadError(`Critical CSV Parsing Error: ${message}`);
                    setIsUploading(false);
                },
            });
        } catch (error) {
            const errorMsg =
                error instanceof Error ? error.message : 'Unknown error';
            console.error('Error starting full upload parse:', error);
            setLogs(prev => [...prev, `Error starting upload: ${errorMsg}`]);
            setUploadError(`Upload Initiation Failed: ${errorMsg}`);
            setIsUploading(false);
        }
    };

    const renderDataTable = (data: RateImportSchema[], title: string) => (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {title}
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Carrier</TableCell>
                            <TableCell>Crop Year</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell>County</TableCell>
                            <TableCell>Township</TableCell>
                            <TableCell>Range</TableCell>
                            <TableCell>Form</TableCell>
                            <TableCell>A</TableCell>
                            <TableCell>B</TableCell>
                            <TableCell>C</TableCell>
                            <TableCell>D</TableCell>
                            <TableCell>E</TableCell>
                            <TableCell>F</TableCell>
                            <TableCell>G</TableCell>
                            <TableCell>H</TableCell>
                            <TableCell>I</TableCell>
                            <TableCell>J</TableCell>
                            <TableCell>K</TableCell>
                            <TableCell>L</TableCell>
                            <TableCell>M</TableCell>
                            <TableCell>N</TableCell>
                            <TableCell>O</TableCell>
                            <TableCell>P</TableCell>
                            <TableCell>P1</TableCell>
                            <TableCell>P2</TableCell>
                            <TableCell>A1</TableCell>
                            <TableCell>A2</TableCell>
                            <TableCell>R1</TableCell>
                            <TableCell>R2</TableCell>
                            <TableCell>E2</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.slice(0, 150).map((row, index) => (
                            <TableRow key={index}>
                                <TableCell>{row.carrier || '-'}</TableCell>
                                <TableCell>{row.cropyear || '-'}</TableCell>
                                <TableCell>{row.state || '-'}</TableCell>
                                <TableCell>{row.county || '-'}</TableCell>
                                <TableCell>{row.township || '-'}</TableCell>
                                <TableCell>{row.range || '-'}</TableCell>
                                <TableCell>{row.form || '-'}</TableCell>
                                <TableCell>{row.a || '-'}</TableCell>
                                <TableCell>{row.b || '-'}</TableCell>
                                <TableCell>{row.c || '-'}</TableCell>
                                <TableCell>{row.d || '-'}</TableCell>
                                <TableCell>{row.e || '-'}</TableCell>
                                <TableCell>{row.f || '-'}</TableCell>
                                <TableCell>{row.g || '-'}</TableCell>
                                <TableCell>{row.h || '-'}</TableCell>
                                <TableCell>{row.i || '-'}</TableCell>
                                <TableCell>{row.j || '-'}</TableCell>
                                <TableCell>{row.k || '-'}</TableCell>
                                <TableCell>{row.l || '-'}</TableCell>
                                <TableCell>{row.m || '-'}</TableCell>
                                <TableCell>{row.n || '-'}</TableCell>
                                <TableCell>{row.o || '-'}</TableCell>
                                <TableCell>{row.p || '-'}</TableCell>
                                <TableCell>{row.p1 || '-'}</TableCell>
                                <TableCell>{row.p2 || '-'}</TableCell>
                                <TableCell>{row.a1 || '-'}</TableCell>
                                <TableCell>{row.a2 || '-'}</TableCell>
                                <TableCell>{row.r1 || '-'}</TableCell>
                                <TableCell>{row.r2 || '-'}</TableCell>
                                <TableCell>{row.e2 || '-'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const renderCropClassesTable = (
        data: CropClassImportSchema[],
        title: string
    ) => (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {title}
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Crop</TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell>AIP</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.slice(0, 150).map((row, index) => (
                            <TableRow key={index}>
                                <TableCell>{row.crop || '-'}</TableCell>
                                <TableCell>{row.class || '-'}</TableCell>
                                <TableCell>{row.state || '-'}</TableCell>
                                <TableCell>{row.aip || '-'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const renderCropCoveragesTable = (
        data: CropCoverageImportSchema[],
        title: string
    ) => (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {title}
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Coverage</TableCell>
                            <TableCell>Coverage Alias</TableCell>
                            <TableCell>Definition</TableCell>
                            <TableCell>Tags</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.slice(0, 150).map((row, index) => (
                            <TableRow key={index}>
                                <TableCell>{row.coverage || '-'}</TableCell>
                                <TableCell>
                                    {row.coverage_alias?.join(', ') || '-'}
                                </TableCell>
                                <TableCell>{row.definition || '-'}</TableCell>
                                <TableCell>
                                    {row.tags?.join(', ') || '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const handleResetClick = (e?: React.MouseEvent<HTMLAnchorElement>) => {
        e?.preventDefault();
        setFile(null);
        setPreviewData([]);
        setLogs([]);
        setIsUploading(false);
        setUploadError(null);
        setUploadSuccess(false);
        setPreviewError(null);
    };

    const handleToggleCheck = async (
        filename: string,
        currentStatus: string
    ) => {
        const newStatus = currentStatus === 'checked' ? 'todo' : 'checked';
        // Optimistic update
        setSheetStatuses(prev => ({
            ...prev,
            [filename]: {
                ...prev[filename],
                id: filename,
                filename,
                status: newStatus,
                last_updated: new Date().toISOString(),
            } as RateSheetStatus,
        }));
        try {
            const { error } = await supabase.rpc('update_rate_sheet_status', {
                p_filename: filename,
                p_status: newStatus,
            });
            if (error) throw error;
        } catch (err) {
            console.error('Failed to save check status:', err);
            // Revert optimistic update
            setSheetStatuses(prev => ({
                ...prev,
                [filename]: {
                    ...prev[filename],
                    id: filename,
                    filename,
                    status: currentStatus,
                } as RateSheetStatus,
            }));
        }
    };

    // Update component to display spreadsheet statuses with selection highlight
    const SpreadsheetStatusList = ({
        statuses,
        isLoading,
        selectedFilename,
        onToggleCheck,
    }: {
        statuses: Record<string, RateSheetStatus>;
        isLoading: boolean;
        selectedFilename: string | null;
        onToggleCheck: (filename: string, currentStatus: string) => void;
    }) => {
        if (isLoading) {
            return <CircularProgress />;
        }
        const groupedFilenames = groupSpreadsheets(REQUIRED_SPREADSHEETS);

        return (
            <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Spreadsheet Upload Status
                </Typography>
                <Grid container spacing={2}>
                    {Object.entries(groupedFilenames).map(
                        ([groupName, filenames]) => (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={3}
                                key={groupName}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, fontWeight: 'bold' }}
                                >
                                    {groupName}
                                </Typography>
                                <List dense disablePadding>
                                    {filenames.map(filename => {
                                        const statusInfo = statuses[filename];
                                        const status =
                                            statusInfo?.status || 'todo';
                                        const isSelected =
                                            filename === selectedFilename &&
                                            status === 'todo';
                                        const canToggle =
                                            status === 'todo' ||
                                            status === 'checked';
                                        let icon;
                                        let color: ChipProps['color'] =
                                            'default';
                                        let displayStatus =
                                            status.toUpperCase();

                                        if (isSelected) {
                                            displayStatus = 'SELECTED';
                                            color = 'info';
                                            icon = (
                                                <TodoIcon
                                                    sx={{
                                                        fontSize: '1.1rem',
                                                    }}
                                                />
                                            );
                                        } else if (status === 'checked') {
                                            icon = (
                                                <CheckedIcon
                                                    color="primary"
                                                    sx={{
                                                        fontSize: '1.1rem',
                                                    }}
                                                />
                                            );
                                            displayStatus = 'DONE';
                                            color = 'primary';
                                        } else if (status === 'uploaded') {
                                            icon = (
                                                <CheckIcon
                                                    color="success"
                                                    sx={{
                                                        fontSize: '1.1rem',
                                                    }}
                                                />
                                            );
                                            color = 'success';
                                        } else if (status === 'error') {
                                            icon = (
                                                <ErrorIcon
                                                    color="error"
                                                    sx={{
                                                        fontSize: '1.1rem',
                                                    }}
                                                />
                                            );
                                            color = 'error';
                                        } else {
                                            icon = (
                                                <TodoIcon
                                                    color="disabled"
                                                    sx={{
                                                        fontSize: '1.1rem',
                                                    }}
                                                />
                                            );
                                        }

                                        const iconElement = canToggle ? (
                                            <Tooltip
                                                title={
                                                    status === 'checked'
                                                        ? 'Mark as not done'
                                                        : 'Mark as done'
                                                }
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        onToggleCheck(
                                                            filename,
                                                            status
                                                        )
                                                    }
                                                    sx={{
                                                        p: 0.25,
                                                        mr: 0.25,
                                                    }}
                                                >
                                                    {icon}
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    mr: 0.5,
                                                }}
                                            >
                                                {icon}
                                            </Box>
                                        );

                                        return (
                                            <ListItem
                                                key={filename}
                                                disablePadding
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'center',
                                                    mb: 0.5,
                                                    bgcolor: isSelected
                                                        ? 'info.lighter'
                                                        : 'transparent',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        flexGrow: 1,
                                                        mr: 1,
                                                    }}
                                                >
                                                    {iconElement}
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            lineHeight: 1.2,
                                                            textDecoration:
                                                                status ===
                                                                'checked'
                                                                    ? 'line-through'
                                                                    : 'none',
                                                            color:
                                                                status ===
                                                                'checked'
                                                                    ? 'text.secondary'
                                                                    : 'text.primary',
                                                        }}
                                                    >
                                                        {filename}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={displayStatus}
                                                    size="small"
                                                    color={color}
                                                    variant={
                                                        isSelected ||
                                                        status === 'checked'
                                                            ? 'filled'
                                                            : 'outlined'
                                                    }
                                                    sx={{
                                                        height: '20px',
                                                        fontSize: '0.65rem',
                                                    }}
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Grid>
                        )
                    )}
                </Grid>
            </Paper>
        );
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#e3f1f0', borderRadius: 1 }}>
            {/* Rate Management Title */}
            <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}
            >
                <Typography variant="h5">Rate Management</Typography>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Upload Rates
                </Typography>

                <Paper
                    {...getRootProps()}
                    sx={{
                        p: 3,
                        border: '2px dashed',
                        borderColor: isDragActive ? 'primary.main' : 'grey.300',
                        bgcolor: isDragActive
                            ? 'action.hover'
                            : 'background.paper',
                        cursor: isUploading ? 'default' : 'pointer',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        mt: 3,
                    }}
                >
                    <input {...getInputProps()} />
                    {!isUploading && (
                        <>
                            <UploadIcon
                                sx={{ fontSize: 40, color: 'action.active' }}
                            />
                            <Typography>
                                {isDragActive
                                    ? 'Drop the CSV file here'
                                    : file
                                      ? `Selected: ${file.name}`
                                      : 'Drag and drop a CSV file here, or click to select file'}
                            </Typography>
                            {!file && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Only CSV files are accepted
                                </Typography>
                            )}
                            {file && isPreviewLoading && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Generating preview...
                                </Typography>
                            )}
                            {file && previewError && !isPreviewLoading && (
                                <Typography variant="caption" color="error">
                                    {previewError}
                                </Typography>
                            )}
                            {file &&
                                !isPreviewLoading &&
                                !previewError &&
                                previewData.length > 0 && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Preview ready below.
                                    </Typography>
                                )}
                        </>
                    )}
                    {isUploading && (
                        <Stack gap={1} sx={{ mt: 2, alignItems: 'center' }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" component="div">
                                Uploading...
                                <strong>
                                    {' '}
                                    {processedRecordCount} / {totalRecordCount}{' '}
                                </strong>
                                records processed.
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                sx={{ width: '100%', mt: 1 }}
                            />
                        </Stack>
                    )}
                </Paper>
            </Paper>

            <Box sx={{ mt: 3 }}>
                <Typography variant="h6">Logs</Typography>
                <List>
                    {logs.map((log, index) => (
                        <ListItem key={index}>{log}</ListItem>
                    ))}
                </List>
            </Box>

            {/* Preview Section */}
            {!isUploading && previewData.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    {/* Display determined parser or warning */}
                    {determinedParser &&
                        determinedParser.key !== 'unmatched' && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Applying Algorithm:
                                <strong>
                                    {determinedParser.key === 'AgriSompo' &&
                                    selectedFilename
                                        ? selectedFilename.includes('-ID-') ||
                                          selectedFilename.includes('-WA-')
                                            ? `${determinedParser.display} (ID/WA Algorithm)`
                                            : `${determinedParser.display} (OR/MT/Other Algorithm)`
                                        : determinedParser.display}
                                </strong>
                            </Typography>
                        )}
                    {uploadBlockedReason && (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            {uploadBlockedReason}
                        </Alert>
                    )}
                    <Box
                        sx={{
                            mt: 2,
                            display: 'flex',
                            justifyContent: 'flex-start',
                            gap: 2,
                        }}
                    >
                        <RaButton
                            label="Confirm and Upload All"
                            variant="contained"
                            color="primary"
                            onClick={handleStartFullImport}
                            disabled={
                                !file ||
                                isUploading ||
                                isPreviewLoading ||
                                !!uploadBlockedReason
                            }
                        />{' '}
                        {/* Disable if blocked */}
                        <RaButton
                            label="Cancel"
                            variant="outlined"
                            color="secondary"
                            onClick={() => handleResetClick()}
                            sx={{ ml: 2 }}
                        />
                    </Box>
                    {determinedParser?.type === 'cropClasses' &&
                        renderCropClassesTable(
                            previewData as CropClassImportSchema[],
                            'Preview Crop Classes Data'
                        )}
                    {determinedParser?.type === 'cropCoverages' &&
                        renderCropCoveragesTable(
                            previewData as CropCoverageImportSchema[],
                            'Preview Crop Coverages Data'
                        )}
                    {determinedParser?.type === 'rates' &&
                        renderDataTable(
                            previewData as RateImportSchema[],
                            'Preview Rates Data'
                        )}
                </Box>
            )}

            {!isUploading && uploadError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {uploadError}
                </Alert>
            )}

            {!isUploading && uploadSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    Upload completed successfully.
                </Alert>
            )}

            {previewError && !isUploading && !file && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {previewError}
                </Alert>
            )}

            {/* Display Statuses Last */}
            <SpreadsheetStatusList
                statuses={sheetStatuses}
                isLoading={statusesLoading}
                selectedFilename={selectedFilename}
                onToggleCheck={handleToggleCheck}
            />
        </Box>
    );
};

RateManagementPage.path = '/hail-pricing-tool/rate-manager';

export default RateManagementPage;
